import {
    useComputed,
    useSignal,
    useSignalEffect,
    useSignals,
} from '@preact/signals-react/runtime'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { MultiSelect } from 'primereact/multiselect'
import { Sidebar } from 'primereact/sidebar'
import { Tag } from 'primereact/tag'
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import PhoneInput from 'react-phone-input-2'
import { SystemRole } from '../../Constants'
import { useAppConfig } from '../../context/app/AppContext'
import { useAppConfigurationCRUD } from '../../context/app/query-keys/AppQueryManager'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import { capitalize, validateEmail } from '../../utils/CommonUtils'
import { useAuth } from '../authentication/AuthProvider'
import { getClientId } from '../authentication/AuthUtils'
import FormHeader from '../form/FormHeader'
import PageContentWrapper from '../layout/PageContentWrapper'
import { isArrayAndNotEmpty } from '../observations/Utils'
import { useListPathways } from '../pathway/query-keys/PathwayKeysManager'
import { usePatientSectionList } from '../patient/query-keys/PatientKeysManager'
import { useStaticListByHospitals } from '../static-list/query-keys/StaticListKeysManager'
import StyledInput from '../styled-component/components/StyledInput'
import { convertKeyToLabel } from '../table-utils/TableUtils'
import {
    healthAssessmentList,
    healthCriteriaDataList,
    healthCriteriaKeys,
    notificationSettingKeys,
    notifyMethodOptions,
    notifyMethodTypeKeys,
    severityNotification,
} from './NotificationConstant'
import { showNotification } from './NotificationService'
import { useNotificationSettingCRUD } from './query-keys/NotificationKeysManager'

const initalData = {
    hospitalIds: [],
    staticListIds: [],
    pathwayIds: [],
    patientIds: [],
    healthCriteriaIds: [],
    selectedNewsParameters: [],
    selectedEmergencyParameters: [],
    otherMethodsType: [],
    otherMethods: {},
}

// TODO translation
export const NotificationSetting = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Context
    const {user} = useAuth()
    const {userRole, hospitalsBelong, notificationSetting} = useAppConfig()

    const hospitalOptions = useMemo(() => {
        if (!hospitalsBelong || hospitalsBelong?.length < 1) return []

        return hospitalsBelong.map(hospital => {
            return {
                label: hospital?.name,
                value: hospital?.id,
            }
        })
    }, [hospitalsBelong])

    const hospitalOptionsIds = useMemo(() => {
        if (!hospitalOptions || hospitalOptions?.length < 1) return []

        return hospitalOptions?.map(hospital => hospital?.value)
    }, [hospitalOptions])
    //#endregion

    //#region Local State
    const {
        translateCommons,
        translateKeys,
        translateOptions,
        translateObservations,
    } = TranslateUtils()

    const inputEmailRef = useRef(null)

    const formData = useSignal({})
    const visible = useSignal(false)
    const inputValue = useSignal({})

    const removeNonExistedItems = (dataList, optionsList) => {
        if (!dataList || dataList?.length < 1) return []

        return dataList?.filter(data => {
            return optionsList?.includes(data)
        })
    }

    //#region Precondition Options
    const {data: staticListOptions, isFetched: isFetchedStaticListOptions} =
        useStaticListByHospitals(user?.id, formData.value?.hospitalIds)

    const staticListOptionsIds = useMemo(() => {
        if (!staticListOptions || staticListOptions?.length < 1) return []

        return staticListOptions?.map(staticList => {
            if (!!staticList?.value) {
                return staticList?.value
            }
        })
    }, [staticListOptions])

    const {data: pathwayOptions, isFetched: isFetchedPathwayOptions} =
        useListPathways(
            user?.id,
            true,
            {
                clientId: getClientId(),
            },
            !!user?.id,
        )

    const pathwayOptionsIds = useMemo(() => {
        if (!pathwayOptions || pathwayOptions?.length < 1) return []

        return pathwayOptions?.map(pathway => {
            if (!!pathway?.value) {
                return pathway?.value
            }
        })
    }, [pathwayOptions])
    //#endregion

    const handleShowForm = () => {
        blockOverflownScroll()
        visible.value = true
        formData.value = {
            hospitalIds: isArrayAndNotEmpty(notificationSetting?.hospitals)
                ? removeNonExistedItems(
                      notificationSetting?.hospitals,
                      hospitalOptionsIds,
                  )
                : [],
            staticListIds: isArrayAndNotEmpty(notificationSetting?.staticLists)
                ? removeNonExistedItems(
                      notificationSetting?.staticLists,
                      staticListOptionsIds,
                  )
                : [],
            pathwayIds: isArrayAndNotEmpty(notificationSetting?.pathways)
                ? removeNonExistedItems(
                      notificationSetting?.pathways,
                      pathwayOptionsIds,
                  )
                : [],
            patientIds: notificationSetting?.patients || [],
            healthCriteriaIds: notificationSetting?.healthCriteriaList || [],
            selectedNewsParameters:
                notificationSetting?.healthAssessmentParameters?.news || [],
            selectedEmergencyParameters:
                notificationSetting?.healthAssessmentParameters?.emergency ||
                [],
            otherMethodsType: notificationSetting?.otherMethodsType || [],
            otherMethods: notificationSetting?.otherMethods || {},
        }
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        formData.value = {
            ...initalData,
        }

        visible.value = false
    }
    //#endregion

    //#region Patient Options
    const {data: patientOptions, isFetched: isFetchedPatientOptions} =
        usePatientSectionList(
            user?.id,
            formData.value?.hospitalIds,
            formData.value?.staticListIds,
            formData.value?.pathwayIds,
            userRole === SystemRole.USER_WITH_ROLE ? user?.id : null,
        )

    const patientOptionsIds = useMemo(() => {
        if (!patientOptions || patientOptions?.length < 1) return []

        return patientOptions.flatMap(obj =>
            obj.items ? obj.items.map(item => item.value) : [],
        )
    }, [patientOptions])

    useEffect(() => {
        if (visible.value) {
            if (formData.value?.hospitalIds?.length > 0) {
                if (!hospitalOptionsIds || hospitalOptionsIds?.length < 1) {
                    formData.value = {
                        ...formData.value,
                        hospitalIds: [],
                        patientIds: [],
                    }
                }
            }
            if (formData.value?.patientIds?.length > 0) {
                if (
                    !!isFetchedPatientOptions &&
                    (!patientOptionsIds || patientOptionsIds?.length < 1)
                ) {
                    formData.value = {
                        ...formData.value,
                        patientIds: [],
                    }
                } else {
                    if (patientOptionsIds?.length > 0) {
                        let temp = removeNonExistedItems(
                            formData.value?.patientIds,
                            patientOptionsIds,
                        )
                        formData.value = {
                            ...formData.value,
                            patientIds: isArrayAndNotEmpty(temp)
                                ? [...temp]
                                : [],
                        }
                    } else {
                        formData.value = {
                            ...formData.value,
                            patientIds: formData.value?.patientIds,
                        }
                    }
                }
            }

            if (formData.value?.hospitalIds?.length < 1) {
                formData.value = {
                    ...formData.value,
                    patientIds: [],
                }
            }
        }
    }, [patientOptionsIds, visible.value])
    //#endregion

    //#region Data Actions
    //Global Client Mutation
    const {mutate: mutateAppNotificationSetting} = useAppConfigurationCRUD(
        user?.id,
    )

    // Server Mutation
    const {mutateAsync: mutateNotificationSetting} = useNotificationSettingCRUD(
        user?.id,
        mutateAppNotificationSetting,
    )

    const handleSubmitData = () => {
        let toPostData = {
            userId: user?.id,
            hospitalIdsBean: formData.value?.hospitalIds,
            staticListIdsBean: formData.value?.staticListIds,
            pathwayIdsBean: formData.value?.pathwayIds,
            patientIdsBean: formData.value?.patientIds,
            healthCriteriaBean: formData.value?.healthCriteriaIds,
            healthAssessmentParametersBean: {
                news: formData.value?.selectedNewsParameters,
                emergency: formData.value?.selectedEmergencyParameters,
            },
            otherMethodsBean: formData.value?.otherMethods,
            otherMethodsTypeBean: formData.value?.otherMethodsType,
        }

        mutateNotificationSetting(toPostData)
        handleHideForm()
    }

    const handleOnChangeParameterValue = (type, value) => {
        if (!type) return

        const key = `selected${capitalize(type)}Parameters`
        const selectedDataList = formData.value[key] || []

        if (selectedDataList?.length > 0) {
            if (selectedDataList?.includes(value)) {
                formData.value = {
                    ...formData.value,
                    [key]: selectedDataList?.filter(e => e !== value),
                }
            } else {
                formData.value = {
                    ...formData.value,
                    [key]: [...selectedDataList, value],
                }
            }
        } else {
            formData.value = {
                ...formData.value,
                [key]: [value],
            }
        }
    }

    const handleOnChangeMethodsType = (key, value) => {
        if (!value) {
            return
        }

        if (!formData.value) {
            formData.value = {
                [key]: [value],
            }

            return
        }

        if (!formData.value[key] || !formData.value[key]?.length) {
            formData.value = {
                ...formData.value,
                [key]: [value],
            }
            return
        }

        if (!formData.value[key].includes(value)) {
            formData.value = {
                ...formData.value,
                [key]: [...formData.value[key], value],
            }
        } else {
            let filterList = formData.value[key]?.filter(item => item !== value)
            formData.value = {
                ...formData.value,
                [key]: [...filterList],
            }
        }
    }

    const handleOnChangeOtherMethods = methodType => {
        if (
            !formData.value ||
            !formData.value[notificationSettingKeys.otherMethodsType]?.length
        ) {
            return
        }

        if (!formData.value[notificationSettingKeys.otherMethods]) {
            formData.value = {
                ...formData.value,
                [notificationSettingKeys.otherMethods]: {
                    [methodType]: [inputValue.value[methodType]],
                },
            }
        } else if (
            !formData.value[notificationSettingKeys.otherMethods][methodType]
        ) {
            formData.value = {
                ...formData.value,
                [notificationSettingKeys.otherMethods]: {
                    ...formData.value[notificationSettingKeys.otherMethods],
                    [methodType]: [inputValue.value[methodType]],
                },
            }
        } else {
            if (
                formData.value[notificationSettingKeys.otherMethods][
                    methodType
                ].includes(inputValue.value[methodType])
            ) {
                showNotification(
                    severityNotification.info,
                    `You have already added this ${
                        methodType === notifyMethodTypeKeys.email
                            ? 'Email'
                            : 'Phone Number'
                    }.`,
                )
            }

            const listHolder = new Set([
                ...formData.value[notificationSettingKeys.otherMethods][
                    methodType
                ],
                inputValue.value[methodType],
            ])
            formData.value = {
                ...formData.value,
                [notificationSettingKeys.otherMethods]: {
                    ...formData.value[notificationSettingKeys.otherMethods],
                    [methodType]: [...listHolder],
                },
            }
        }

        inputValue.value = {
            ...inputValue.value,
            [methodType]: '',
        }

        if (
            inputEmailRef.current &&
            methodType === notifyMethodTypeKeys.email
        ) {
            inputEmailRef.current.changeValue('')
        }
        return
    }

    const handleOnDeleteNotifyMethod = (notifyMethodType, value) => {
        if (
            !formData.value ||
            !formData.value[notificationSettingKeys.otherMethods] ||
            !formData.value[notificationSettingKeys.otherMethods][
                notifyMethodType
            ]
        ) {
            return
        }

        formData.value = {
            ...formData.value,
            [notificationSettingKeys.otherMethods]: {
                ...formData.value[notificationSettingKeys.otherMethods],
                [notifyMethodType]: formData.value[
                    notificationSettingKeys.otherMethods
                ][notifyMethodType].filter(item => item !== value),
            },
        }
    }
    //#endregion

    //#region Render Template
    const groupedItemTemplate = option => {
        return (
            <div className="flex align-items-center">
                <div>
                    {translateCommons('From Hospital')} {option.label}{' '}
                    <span className="text-darkcyan">
                        ({option?.items?.length} {translateCommons('options')})
                    </span>
                </div>
            </div>
        )
    }

    const renderRowItem = (title, subtitle, children) => {
        return (
            <div
                className={'p-0 mb-5'}
                style={{borderBottom: '1px dashed lightgray'}}>
                <div className="grid p-0" style={{position: 'relative'}}>
                    <div
                        className={
                            !!children
                                ? `md:col-4 col-12 sticky`
                                : 'col-12 sticky'
                        }
                        style={!title ? {top: 0} : {}}>
                        {!!title && (
                            <div className="mb-2">
                                <h4 className="mb-1 font-bold underline-60">
                                    {translateCommons(title)}
                                </h4>
                            </div>
                        )}
                        {subtitle && typeof subtitle === 'string' ? (
                            <p>{translateCommons(subtitle)}</p>
                        ) : (
                            <div>{subtitle}</div>
                        )}
                    </div>

                    {!!children && (
                        <div className="md:col-8 col-12">{children}</div>
                    )}
                </div>
            </div>
        )
    }

    const renderOptionItemCondition = useCallback(
        type => {
            return (
                <div className="col-12 p-0 mb-3">
                    <h5 className="font-bold p-0 m-0">
                        {translateCommons(
                            `Health Assessment Parameters (${
                                type ? type.toUpperCase() : ''
                            })`,
                        )}
                    </h5>
                    <div className="flex align-items-center justify-content-start flex-wrap">
                        {translateOptions(healthAssessmentList)?.map(item => (
                            <div
                                key={item.value}
                                className="field-checkbox m-2">
                                <Checkbox
                                    name={item.label}
                                    value={item.value}
                                    onChange={e =>
                                        handleOnChangeParameterValue(
                                            type,
                                            e.value,
                                        )
                                    }
                                    checked={formData.value[
                                        `selected${capitalize(type)}Parameters`
                                    ]?.includes(item.value)}
                                />
                                <label htmlFor={item.label}>{item.label}</label>
                            </div>
                        ))}
                    </div>
                </div>
            )
        },
        [translateCommons, translateOptions],
    )

    const panelVisible = useSignal(false)

    const renderOptionItem = useCallback(
        ({
            label = '',
            key = '',
            options = [],
            placeholder = '',
            required = false,
            ...restProps
        }) => {
            return (
                <div className="mb-3">
                    {!!label && (
                        <div className="w-full flex justify-content-between align-items-center mb-2">
                            {!!required ? (
                                <React.Fragment>
                                    <div>
                                        <h5 className="font-bold p-0 m-0">
                                            {translateCommons(label)}
                                        </h5>
                                    </div>
                                    <div>
                                        <Tag
                                            value={translateCommons(
                                                'This field is required !',
                                            )}
                                            severity="danger"
                                            icon="pi pi-info-circle"></Tag>
                                    </div>
                                </React.Fragment>
                            ) : (
                                <h5 className="font-bold p-0 m-0">
                                    {translateCommons(label)}
                                </h5>
                            )}
                        </div>
                    )}
                    <MultiSelect
                        key={key}
                        className="col-12 mb-3 font-bold"
                        options={
                            options?.length > 0 ? translateOptions(options) : []
                        }
                        value={
                            options?.length > 0 &&
                            isArrayAndNotEmpty(formData.value[key])
                                ? formData.value[key]
                                : []
                        }
                        onChange={e => {
                            if (!!key) {
                                formData.value = {
                                    ...formData.value,
                                    [key]: e.value,
                                }
                            }
                        }}
                        maxSelectedLabels={3}
                        placeholder={translateCommons(placeholder)}
                        optionLabel="label"
                        optionValue="value"
                        emptyFilterMessage={translateCommons(
                            'No results found',
                        )}
                        display={
                            isArrayAndNotEmpty(formData.value[key]) &&
                            formData.value[key]?.length < 4
                                ? 'chip'
                                : 'comma'
                        }
                        filter
                        filterBy="label"
                        filterPlaceholder={translateCommons('Search here')}
                        resetFilterOnHide={true}
                        showClear={false}
                        panelFooterTemplate={
                            <div
                                className="p-3"
                                style={{backgroundColor: '#f9f9f9'}}>
                                <b>
                                    {formData.value[key]?.length > 0
                                        ? `${
                                              formData.value[key]?.length
                                          } ${translateCommons(
                                              formData.value[key]?.length > 1
                                                  ? 'items selected'
                                                  : 'item selected',
                                          )}`
                                        : translateCommons('No item selected')}
                                </b>
                            </div>
                        }
                        maxLength={3}
                        onShow={() => (panelVisible.value = true)}
                        onHide={() => (panelVisible.value = false)}
                        {...restProps}
                    />
                    {key === 'patientIds' && (
                        <p>
                            <i className="pi pi-exclamation-circle" />{' '}
                            {translateCommons(
                                'Patient options only display the list of patients managed by you',
                            )}
                        </p>
                    )}
                    {key === 'healthCriteriaIds' ? (
                        <React.Fragment>
                            {formData.value['healthCriteriaIds']?.includes(
                                'news',
                            ) && renderOptionItemCondition('news')}
                            {formData.value['healthCriteriaIds']?.includes(
                                'emergency',
                            ) && renderOptionItemCondition('emergency')}
                        </React.Fragment>
                    ) : null}
                </div>
            )
        },
        [translateCommons],
    )

    const renderSMSOptions = useCallback(() => {
        return (
            <div className="flex flex-column ">
                <h5 className="font-bold m-0 pb-2">SMS Notification</h5>
                <div className="">
                    <p className="text-800 w-full my-0 font-bold">
                        Phone Number
                    </p>
                    <div className="flex align-items-center mb-2">
                        <PhoneInput
                            className={`p-0 font-semibold mr-2`}
                            inputClass={'font-semibold'}
                            enableSearch
                            country={'gb'}
                            preferredCountries={['gb', 'us', 'ca', 'vn', 'bg']}
                            inputStyle={{
                                minHeight: '40px',
                            }}
                            alwaysDefaultMask={true}
                            placeholder="Enter the phone number whose you want to send the SMS notification to."
                            onChange={(value, country, e, formattedData) => {
                                inputValue.value = {
                                    ...inputValue.value,
                                    [notifyMethodTypeKeys.sms]: formattedData,
                                }
                            }}
                            value={
                                inputValue.value[notifyMethodTypeKeys.sms] ||
                                '44'
                            }
                        />
                        <Button
                            className=""
                            icon={'pi pi-plus'}
                            onClick={() =>
                                handleOnChangeOtherMethods(
                                    notifyMethodTypeKeys.sms,
                                )
                            }
                            disabled={
                                inputValue.value[notifyMethodTypeKeys.sms]
                                    ?.length < 10
                            }
                        />
                    </div>
                    {user?.phone?.length > 9 &&
                        formData.value &&
                        formData.value[notificationSettingKeys.otherMethods] &&
                        formData.value[notificationSettingKeys.otherMethods][
                            notifyMethodTypeKeys.sms
                        ] &&
                        !formData.value[notificationSettingKeys.otherMethods][
                            notifyMethodTypeKeys.sms
                        ]?.includes(user?.phone) && (
                            <div className="p-2 border-round border-1 border-green-200 bg-green-50 flex align-items-center justify-content-between">
                                <small className="text-800 w-full my-0 font-semibold">
                                    Do you want to add the current phone number{' '}
                                    <span className="text-primary">
                                        ({user?.phone})
                                    </span>{' '}
                                    to receive notifications?
                                </small>
                                <p
                                    className="text-primary my-0 font-bold cursor-pointer"
                                    onClick={() => {
                                        inputValue.value = {
                                            ...inputValue.value,
                                            [notifyMethodTypeKeys.sms]:
                                                user?.phone,
                                        }

                                        handleOnChangeOtherMethods(
                                            notifyMethodTypeKeys.sms,
                                        )
                                    }}>
                                    Add
                                </p>
                            </div>
                        )}
                </div>
                {!!inputValue.value[notifyMethodTypeKeys.sms] &&
                    inputValue.value[notifyMethodTypeKeys.sms]?.length < 10 && (
                        <small
                            style={{
                                color: 'red',
                            }}>
                            Invalid phone number.
                        </small>
                    )}
                <div className="mt-2">
                    <p className="text-800 w-full my-0 font-bold">
                        Current recipient phone number list:
                    </p>
                    {!formData.value[
                        notificationSettingKeys.otherMethodsType
                    ] ||
                    !formData.value[
                        notificationSettingKeys.otherMethodsType
                    ].includes(notifyMethodTypeKeys.sms) ||
                    !formData.value[notificationSettingKeys.otherMethods] ||
                    !formData.value[notificationSettingKeys.otherMethods][
                        notifyMethodTypeKeys.sms
                    ] ||
                    formData.value[notificationSettingKeys.otherMethods][
                        notifyMethodTypeKeys.sms
                    ]?.length < 1 ? (
                        'There are currently no recipient phone number added.'
                    ) : (
                        <ul className="list-disc">
                            {formData.value[
                                notificationSettingKeys.otherMethods
                            ][notifyMethodTypeKeys.sms]?.map(
                                sms =>
                                    sms && (
                                        <li key={sms}>
                                            <div className="flex align-items-center">
                                                {sms}{' '}
                                                <i
                                                    className="pi pi-times p-2 ml-2 text-xl border-circle hover:surface-100 cursor-pointer"
                                                    style={{color: 'red'}}
                                                    onClick={() =>
                                                        handleOnDeleteNotifyMethod(
                                                            notifyMethodTypeKeys.sms,
                                                            sms,
                                                        )
                                                    }></i>
                                            </div>
                                        </li>
                                    ),
                            )}
                        </ul>
                    )}
                </div>
            </div>
        )
    }, [formData.value, inputValue])

    const renderEmailOptions = useCallback(() => {
        return (
            <div className="flex flex-column ">
                <h5 className="font-bold m-0 pb-2">Email Notification</h5>
                <div className="flex flex-column align-items-between ">
                    <div>
                        <p className="text-800 w-full my-0 font-bold">
                            Recipient Email
                        </p>
                        <div className="flex align-items-center mb-2">
                            <StyledInput
                                ref={inputEmailRef}
                                containerClassName="w-full mr-2"
                                value={
                                    inputValue.value[
                                        notifyMethodTypeKeys.email
                                    ] || ''
                                }
                                placeholder={
                                    'Enter the email whose you want to send the notification to.'
                                }
                                onChange={value =>
                                    (inputValue.value = {
                                        ...inputValue.value,
                                        [notifyMethodTypeKeys.email]: value,
                                    })
                                }
                            />

                            <Button
                                className=""
                                icon={'pi pi-plus'}
                                disabled={
                                    !validateEmail(
                                        inputValue.value[
                                            notifyMethodTypeKeys.email
                                        ],
                                    )
                                }
                                onClick={() =>
                                    handleOnChangeOtherMethods(
                                        notifyMethodTypeKeys.email,
                                    )
                                }
                            />
                        </div>
                        {user?.email?.length > 9 &&
                            formData.value &&
                            formData.value[
                                notificationSettingKeys.otherMethods
                            ] &&
                            formData.value[
                                notificationSettingKeys.otherMethods
                            ][notifyMethodTypeKeys.email] &&
                            !formData.value[
                                notificationSettingKeys.otherMethods
                            ][notifyMethodTypeKeys.email]?.includes(
                                user?.email,
                            ) && (
                                <div className="p-2 border-round border-1 border-green-200 bg-green-50 flex align-items-center justify-content-between">
                                    <small className="text-800 w-full my-0 font-semibold">
                                        Do you want to add the current email{' '}
                                        <span className="text-primary">
                                            ({user?.email})
                                        </span>{' '}
                                        to receive notifications?
                                    </small>
                                    <p
                                        className="text-primary my-0 font-bold cursor-pointer"
                                        onClick={() => {
                                            inputValue.value = {
                                                ...inputValue.value,
                                                [notifyMethodTypeKeys.email]:
                                                    user?.email,
                                            }

                                            handleOnChangeOtherMethods(
                                                notifyMethodTypeKeys.email,
                                            )
                                        }}>
                                        Add
                                    </p>
                                </div>
                            )}
                    </div>
                    {!!inputValue.value[notifyMethodTypeKeys.email] &&
                        !validateEmail(
                            inputValue.value[notifyMethodTypeKeys.email],
                        ) && (
                            <small
                                style={{
                                    color: 'red',
                                }}>
                                Invalid email.
                            </small>
                        )}

                    <div className="mt-2">
                        <p className="text-800 w-full my-0 font-bold">
                            Current recipient email list:
                        </p>
                        {!formData.value[
                            notificationSettingKeys.otherMethodsType
                        ] ||
                        !formData.value[
                            notificationSettingKeys.otherMethodsType
                        ].includes(notifyMethodTypeKeys.email) ||
                        !formData.value[notificationSettingKeys.otherMethods] ||
                        !formData.value[notificationSettingKeys.otherMethods][
                            notifyMethodTypeKeys.email
                        ] ||
                        formData.value[notificationSettingKeys.otherMethods][
                            notifyMethodTypeKeys.email
                        ]?.length < 1 ? (
                            'There are currently no recipient emails added.'
                        ) : (
                            <ul className="list-disc">
                                {formData.value[
                                    notificationSettingKeys.otherMethods
                                ][notifyMethodTypeKeys.email]?.map(
                                    email =>
                                        email && (
                                            <li key={email}>
                                                <div className="flex align-items-center">
                                                    {email}{' '}
                                                    <i
                                                        className="pi pi-times p-2 ml-2 text-xl border-circle hover:surface-100 cursor-pointer"
                                                        style={{color: 'red'}}
                                                        onClick={() =>
                                                            handleOnDeleteNotifyMethod(
                                                                notifyMethodTypeKeys.email,
                                                                email,
                                                            )
                                                        }></i>
                                                </div>
                                            </li>
                                        ),
                                )}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        )
    }, [formData.value, inputValue])

    const renderAdditionalMethodSection = useCallback(() => {
        return (
            <div className="mb-3">
                {/* Other Methods Type */}
                <div className="flex flex-column mb-2">
                    <div className="w-full mb-2">
                        <h5 className="font-bold p-0 m-0">
                            {translateCommons('Notify Method Type Options')}
                        </h5>
                    </div>
                    <div className="flex align-items-center">
                        {notifyMethodOptions.map(item => (
                            <div
                                key={item.value}
                                className={`flex align-items-center justify-content-between m-2 p-2 border-round border-2 ${
                                    formData.value[
                                        notificationSettingKeys.otherMethodsType
                                    ] &&
                                    formData.value[
                                        notificationSettingKeys.otherMethodsType
                                    ].includes(item.value)
                                        ? 'border-primary text-primary'
                                        : 'border-400'
                                } ${'select-auto cursor-pointer'}`}
                                onClick={() => {
                                    handleOnChangeMethodsType(
                                        notificationSettingKeys.otherMethodsType,
                                        item.value,
                                    )
                                }}>
                                <div className="flex align-items-center mr-5">
                                    <label
                                        className={`mx-2 ${'select-auto cursor-pointer'}`}>
                                        {item.label}
                                    </label>
                                </div>
                                <Checkbox
                                    inputId={item.value}
                                    name={item.value}
                                    value={item.value}
                                    checked={
                                        formData.value[
                                            notificationSettingKeys
                                                .otherMethodsType
                                        ] &&
                                        formData.value[
                                            notificationSettingKeys
                                                .otherMethodsType
                                        ].includes(item.value)
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {formData.value[notificationSettingKeys.otherMethodsType]
                    ?.length > 0 && (
                    <div className="grid align-items-stretch mb-2 gap-2">
                        {formData.value[
                            notificationSettingKeys.otherMethodsType
                        ].includes(notifyMethodTypeKeys.sms) ? (
                            <div className="col-12 md:col p-0">
                                {renderSMSOptions()}
                            </div>
                        ) : (
                            ''
                        )}

                        {formData.value[
                            notificationSettingKeys.otherMethodsType
                        ].includes(notifyMethodTypeKeys.email) ? (
                            <div className="col-12 md:col p-0">
                                {renderEmailOptions()}
                            </div>
                        ) : (
                            ''
                        )}
                    </div>
                )}
            </div>
        )
    }, [formData.value, renderEmailOptions, renderSMSOptions, translateCommons])

    const renderPatientPreCondition = () => {
        return (
            <React.Fragment>
                {renderOptionItem({
                    key: 'staticListIds',
                    label: translateCommons('Static Lists'),
                    options: staticListOptions || [],
                    value: formData.value?.staticListIds,
                    placeholder: translateCommons(
                        'Limit patients who belong to these static lists',
                    ),

                    optionGroupLabel: 'label',
                    optionGroupChildren: 'items',
                    optionGroupTemplate: groupedItemTemplate,
                })}
                {renderOptionItem({
                    key: 'pathwayIds',
                    label: 'Pathways',
                    options: pathwayOptions || [],
                    value: formData.value?.pathwayIds,
                    placeholder: translateCommons(
                        'Limit patients who has been in these pathways',
                    ),
                })}
            </React.Fragment>
        )
    }
    //#endregion

    //#region Prevent Scroll Helper
    useSignalEffect(() => {
        const contentDiv = document.querySelector(
            '#sidebar-notification-setting .p-sidebar-content',
        )

        toggleBlockScroll(contentDiv, panelVisible.value)

        return () => {
            if (!!contentDiv) {
                contentDiv.removeEventListener('wheel', preventScroll)
                contentDiv.removeEventListener('touchmove', preventScroll)
            }
        }
    })

    const preventScroll = event => {
        event.preventDefault()
        event.stopPropagation()
    }

    const toggleBlockScroll = useCallback((contentDiv, isVisible) => {
        if (!!contentDiv) {
            if (!!isVisible) {
                contentDiv.addEventListener('wheel', preventScroll, {
                    passive: false,
                })
                contentDiv.addEventListener('touchmove', preventScroll, {
                    passive: false,
                })
            } else {
                contentDiv.removeEventListener('wheel', preventScroll)
                contentDiv.removeEventListener('touchmove', preventScroll)
            }
        }
    }, [])
    //#endregion

    //#region Computed Summary
    const getShortens = dataList => {
        if (dataList?.length <= 2) {
            return dataList?.join(', ')
        } else {
            return `${dataList?.slice(0, 2).join(', ')} +${
                dataList?.length - 2
            } more`
        }
    }

    const NotificationSettingSummary = useComputed(() => {
        if (formData.value?.hospitalIds?.length < 1)
            return translateCommons('Please select at least one hospital')

        if (formData.value?.patientIds?.length < 1)
            return translateCommons('Please select at least one patient')

        // Prefer to ArrayList Form []
        const hospitalNames = formData.value?.hospitalIds?.map(hospitalId => {
            return hospitalOptions?.find(option => option.value === hospitalId)
                ?.label
        })

        // Prefer to SectionList Form {label: "", items: []}
        const staticListNames = formData.value?.staticListIds?.map(
            staticListId => {
                return staticListOptions?.map(staticListGroup => {
                    return staticListGroup?.items?.find(
                        option => option.value === staticListId,
                    )?.label
                })
            },
        )

        // Prefer to ArrayList Form []
        const pathwayNames = formData.value?.pathwayIds?.map(pathwayId => {
            return pathwayOptions?.find(option => option.value === pathwayId)
                ?.label
        })

        // Prefer to SectionList Form {label: "", items: []}
        const patientNames = formData.value?.patientIds?.map(patientId => {
            return patientOptions?.map(patientGroup => {
                return patientGroup?.items?.find(
                    option => option.value === patientId,
                )?.label
            })
        })

        // Prefer to ArrayList Form []
        const healthCriterias = formData.value?.healthCriteriaIds
            ?.map(healthCriteriaId => {
                return healthCriteriaDataList?.find(
                    option => option?.value === healthCriteriaId,
                )
            })
            ?.filter(item => !!item)

        // Build summary based on hospital, patient, health criteria, and health assessment parameters
        return (
            <div>
                <p>
                    {translateCommons(
                        'You have configured your healthcare system notifications to include updates from these hospitals',
                    )}{' '}
                    <span className="text-darkcyan font-bold">
                        {getShortens(hospitalNames)}.
                    </span>
                    {staticListNames?.length > 0 && (
                        <span>
                            {' '}
                            {translateCommons(
                                'You have also narrowed down the number of patients by your self-defined static list:',
                            )}{' '}
                            <span className="text-darkcyan font-bold">
                                {getShortens(staticListNames)}.
                            </span>
                        </span>
                    )}
                    {pathwayNames?.length > 0 && (
                        <span>
                            {' '}
                            {translateCommons(
                                'and your focus pathways are:',
                            )}{' '}
                            <span className="text-darkcyan font-bold">
                                {pathwayNames.join(', ')}.
                            </span>
                        </span>
                    )}
                    {patientNames?.length > 0 && (
                        <span>
                            {' '}
                            {translateCommons(
                                'The individual patients you are monitoring closely are:',
                            )}{' '}
                            <span className="text-darkcyan font-bold">
                                {getShortens(patientNames)}.
                            </span>{' '}
                            {translateCommons(
                                'Ensure you only receive notifications about patients',
                            )}
                        </span>
                    )}
                </p>
                {healthCriterias?.length > 0 && (
                    <React.Fragment>
                        <p>
                            {translateCommons(
                                'Additionally, you have specified categories including',
                            )}{' '}
                            <span className="text-darkcyan font-bold">
                                {healthCriterias
                                    ?.map(item => item?.label)
                                    ?.join(', ')}
                            </span>{' '}
                            .{' '}
                            {translateCommons(
                                'The health criteria assessments you are tracking include:',
                            )}
                        </p>
                        <div className="grid">
                            {healthCriterias?.map((healthCriteria, idx) => {
                                const healthAssessmentList =
                                    healthCriteria?.value ===
                                    healthCriteriaKeys.news
                                        ? formData?.value
                                              ?.selectedNewsParameters
                                        : healthCriteria?.value ===
                                          healthCriteriaKeys.emergency
                                        ? formData?.value
                                              ?.selectedEmergencyParameters
                                        : []
                                return (
                                    <div
                                        key={`${healthCriteria?.value}-${idx}`}
                                        className="md:col-4 col-12">
                                        <div className="flex align-items-center">
                                            <div className="font-bold">
                                                {translateCommons(
                                                    healthCriteria?.label,
                                                )}
                                            </div>
                                        </div>
                                        <React.Fragment>
                                            {healthAssessmentList?.length >
                                            0 ? (
                                                <ul
                                                    key={`${healthCriteria?.value}-ul`}>
                                                    {healthAssessmentList?.map(
                                                        parameter => (
                                                            <li
                                                                key={`${healthCriteria?.value}-${parameter}`}>
                                                                {parameter
                                                                    ? translateKeys(
                                                                          convertKeyToLabel(
                                                                              parameter,
                                                                          ),
                                                                      )
                                                                    : ''}
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            ) : (
                                                <ul
                                                    key={`${healthCriteria?.value}-ul-none`}>
                                                    <li>
                                                        {healthCriteria?.value ===
                                                        healthCriteriaKeys.fall ? (
                                                            <span>
                                                                {translateCommons(
                                                                    'Receiving all notifcations about patients whenever',
                                                                )}{' '}
                                                                <span className="text-darkcyan font-bold">
                                                                    {translateCommons(
                                                                        'FALL',
                                                                    )}{' '}
                                                                </span>{' '}
                                                                {translateCommons(
                                                                    'events occur',
                                                                )}
                                                            </span>
                                                        ) : healthCriteria.value ===
                                                          healthCriteriaKeys.symptom ? (
                                                            <span>
                                                                {translateCommons(
                                                                    'Receiving all notifcations about patients whenever',
                                                                )}{' '}
                                                                <span className="text-darkcyan font-bold">
                                                                    {translateCommons(
                                                                        'the patient reports a symptom.',
                                                                    )}{' '}
                                                                </span>{' '}
                                                            </span>
                                                        ) : healthCriteria.value ===
                                                          healthCriteriaKeys.conversation ? (
                                                            <span>
                                                                {translateCommons(
                                                                    'Receiving all notifcations about patients whenever',
                                                                )}{' '}
                                                                <span className="text-darkcyan font-bold">
                                                                    {translateCommons(
                                                                        'the patient send a message.',
                                                                    )}{' '}
                                                                </span>{' '}
                                                            </span>
                                                        ) : healthCriteria.value ===
                                                          healthCriteriaKeys.ecg ? (
                                                            <span>
                                                                {translateCommons(
                                                                    'Receiving all notifcations about patients whenever',
                                                                )}{' '}
                                                                <span className="text-darkcyan font-bold">
                                                                    {translateCommons(
                                                                        'ECG',
                                                                    )}{' '}
                                                                </span>{' '}
                                                                {translateCommons(
                                                                    'events occur',
                                                                )}
                                                            </span>
                                                        ) : (
                                                            translateCommons(
                                                                'No item specific',
                                                            )
                                                        )}
                                                    </li>
                                                </ul>
                                            )}
                                        </React.Fragment>
                                    </div>
                                )
                            })}
                        </div>
                    </React.Fragment>
                )}
                <p>
                    {translateCommons(
                        'This comprehensive setup allows you to receive timely and relevant notifications, ensuring you are well-informed about the status and needs of these hospitals, pathways, patients, and specific health criteria.',
                    )}
                </p>
            </div>
        )
    })
    //#endregion

    //#region Render HTML
    return (
        <Sidebar
            id="sidebar-notification-setting"
            className="p-sidebar-full"
            visible={visible.value}
            onHide={handleHideForm}
            baseZIndex={1002}
            blockScroll
            fullScreen>
            <PageContentWrapper>
                <FormHeader
                    header={translateCommons('NOTIFICATION SETTING')}
                    onHide={handleHideForm}
                />
                {/* Render Hospitals */}
                {renderRowItem(
                    'Hospital Information',
                    'Select hospitals from the list below',
                    renderOptionItem({
                        key: 'hospitalIds',
                        label: 'Hospital Options',
                        options: hospitalOptions,
                        placeholder: 'Select hospitals from the list',
                        required: true,
                    }),
                )}

                {/* Render Patient Pre-condition */}
                {renderRowItem(
                    'Patient Pre-condition',
                    <>
                        {translateCommons(
                            'Narrow down the patients you want to receive notifications about.',
                        )}{' '}
                        <br />
                        <span className="text-darkcyan font-bold">
                            {translateCommons(
                                'This will limit the patients options below',
                            )}
                        </span>
                    </>,
                    renderPatientPreCondition(),
                )}

                {/* Render Patients */}
                {renderRowItem(
                    'Patient Information',
                    'Select patients who you will receive notifications about',
                    renderOptionItem({
                        key: 'patientIds',
                        required: true,
                        label: 'Patient Options',
                        options:
                            !!patientOptions?.length > 0 ? patientOptions : [],
                        placeholder: 'Select patients from the list',

                        optionGroupLabel: 'label',
                        optionGroupChildren: 'items',
                        optionGroupTemplate: groupedItemTemplate,
                    }),
                )}

                {/* Render Health Criteria */}
                {renderRowItem(
                    'Health Criteria',
                    'Only receive notifications about patients who meet these health criteria',
                    renderOptionItem({
                        key: 'healthCriteriaIds',
                        label: 'Health Criteria Options',
                        options: healthCriteriaDataList,
                        placeholder: 'Select Health Criteria from the list',
                        required: true,
                    }),
                )}

                {/* Render Additional Methods */}
                {renderRowItem(
                    'Additional Notify Methods',
                    <>
                        {translateCommons(
                            'Add additional methods to send notifications through various channels such as phone number, SMS, etc',
                        )}{' '}
                        <br />
                        <p className="py-2">
                            <i className="pi pi-info-circle mr-2"></i>
                            <span>
                                Note: Notifications are only received when the
                                patient shows signs of
                                {` `}
                                <span className="text-darkcyan font-bold">
                                    FALL
                                    {` `}
                                </span>
                                or{' '}
                                <span className="text-darkcyan font-bold">
                                    EMERGENCY
                                </span>
                                .
                            </span>
                        </p>
                    </>,
                    renderAdditionalMethodSection(),
                )}

                {/* Render Conclusion */}
                {renderRowItem(
                    'Notification Setting Summary',
                    NotificationSettingSummary,
                    null,
                )}

                <div className="flex col-12 py-3">
                    <Button
                        className={`col-12 p-button-lg font-bold mr-2`}
                        icon={'pi pi-send'}
                        label={translateKeys('SUBMIT')}
                        onClick={handleSubmitData}
                    />
                </div>
            </PageContentWrapper>
        </Sidebar>
    )
    //#endregion
})
